import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search, AlertTriangle, RefreshCw, ArrowUp, ArrowDown, ArrowUpDown, X, Cable, Phone, Check,
} from 'lucide-react';
import { onts, olts } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';

// Column definitions — drives both header rendering and filter inputs
type ColumnKey = 'status' | 'services' | 'customer' | 'serial' | 'model' | 'pon' | 'seat' | 'last_seen';
const COLUMNS: Array<{
  key: ColumnKey;
  label: string;
  sortable: boolean;
  sortField?: string;          // backend `sort=...` value
  filterParam?: string;        // backend query param name
  filterKind: 'text' | 'select' | 'olt';
  filterWidth?: string;        // tailwind class for input width
  placeholder?: string;
}> = [
  { key: 'status',    label: 'Status',     sortable: true,  sortField: 'oper_status',     filterParam: 'status',   filterKind: 'select' },
  { key: 'services',  label: 'Services',   sortable: false, filterKind: 'text' },
  { key: 'customer',  label: 'Customer',   sortable: true,  sortField: 'external_id',     filterParam: 'customer', filterKind: 'text',   placeholder: 'name / ext_id' },
  { key: 'serial',    label: 'Serial',     sortable: true,  sortField: 'vendor_serial',   filterParam: 'serial',   filterKind: 'text',   placeholder: 'ADTN…' },
  { key: 'model',     label: 'Model',      sortable: true,  sortField: 'model',           filterParam: 'model',    filterKind: 'text',   placeholder: '411 / SFU…' },
  { key: 'pon',       label: 'OLT / PON',  sortable: false,                                filterParam: 'pon',      filterKind: 'olt',    placeholder: '1/1/1' },
  { key: 'seat',      label: 'Seat',       sortable: true,  sortField: 'vendor_local_id', filterParam: 'seat',     filterKind: 'text',   filterWidth: 'w-14', placeholder: '3' },
  { key: 'last_seen', label: 'Last seen',  sortable: true,  sortField: 'last_seen_at',    filterKind: 'text' },
];

export default function Onts() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [filterRowOpen, setFilterRowOpen] = useState(() =>
    COLUMNS.some((c) => c.filterParam && params.get(c.filterParam))
  );

  // Local input state (debounced into URL)
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [colFilters, setColFilters] = useState<Record<string, string>>(() => {
    const obj: Record<string, string> = {};
    const noParams = !params.toString();
    COLUMNS.forEach((c) => {
      if (!c.filterParam) return;
      // Default: status=up when arriving with no params (Phil prefers seeing live ONTs first)
      if (c.filterParam === 'status' && noParams) {
        obj[c.filterParam] = 'up';
        return;
      }
      obj[c.filterParam] = params.get(c.filterParam) ?? '';
    });
    return obj;
  });
  const linkPending = params.get('link_pending') === 'true';
  const oltId  = params.get('olt_id') ?? '';
  const page   = Number(params.get('page') ?? 1);
  const sort   = params.get('sort') ?? 'id';
  const dir    = (params.get('dir') ?? 'asc') as 'asc' | 'desc';

  const { data: oltList } = useQuery({
    queryKey: ['olts'],
    queryFn: () => olts.list(),
  });

  const { data, isFetching } = useQuery({
    queryKey: ['onts', { search, colFilters, oltId, linkPending, page, sort, dir }],
    queryFn: () => onts.list({
      search: search || undefined,
      olt_id: oltId ? Number(oltId) : undefined,
      link_pending: linkPending ? true : undefined,
      page,
      per_page: 50,
      sort, dir,
      ...Object.fromEntries(Object.entries(colFilters).filter(([, v]) => v)),
    }),
  });

  // Debounce filter inputs → URL params
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (search) next.set('search', search); else next.delete('search');
      COLUMNS.forEach((c) => {
        if (!c.filterParam) return;
        const v = colFilters[c.filterParam];
        if (v) next.set(c.filterParam, v); else next.delete(c.filterParam);
      });
      next.delete('page');  // reset to first page when filters change
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, JSON.stringify(colFilters)]);

  function toggleSort(col: typeof COLUMNS[number]) {
    if (!col.sortable || !col.sortField) return;
    const next = new URLSearchParams(params);
    if (sort === col.sortField) {
      next.set('dir', dir === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', col.sortField);
      next.set('dir', 'asc');
    }
    next.delete('page');
    setParams(next);
  }

  function resetFilters() {
    setSearch('');
    setColFilters(Object.fromEntries(Object.keys(colFilters).map((k) => [k, ''])));
    setParams({}, { replace: true });
  }

  const activeFilterCount = (search ? 1 : 0) + Object.values(colFilters).filter(Boolean).length + (linkPending ? 1 : 0) + (oltId ? 1 : 0);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="ONTs"
        subtitle={`${data?.meta.total ?? '…'} terminaux fibre`}
        actions={<>
          <button
            className={`btn btn-sm ${filterRowOpen ? 'btn-primary' : ''}`}
            onClick={() => setFilterRowOpen((v) => !v)}
            title="Afficher/masquer les filtres par colonne"
          >
            <Search size={11} />Filtres {activeFilterCount > 0 && <span className="ml-1 px-1 rounded bg-current/20">{activeFilterCount}</span>}
          </button>
          {activeFilterCount > 0 && (
            <button className="btn btn-sm" onClick={resetFilters} title="Réinitialiser tous les filtres">
              <X size={11} />Reset
            </button>
          )}
        </>}
      />

      {/* Toolbar : global search + status + OLT + link_pending + reset */}
      <div className="px-4 py-2 flex items-center gap-2 border-b border-border bg-bg-2 shrink-0 flex-wrap">
        <div className="relative">
          <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-faint pointer-events-none" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="recherche globale (serial, customer, phone, ext_id, model)"
            className="input w-80 pl-7 text-xs"
          />
        </div>
        <select
          className="input w-40 text-xs"
          value={colFilters.status ?? ''}
          onChange={(e) => setColFilters({ ...colFilters, status: e.target.value })}
        >
          <option value="">Tous statuts</option>
          <option value="up">up</option>
          <option value="down">down</option>
          <option value="discovering">discovering</option>
        </select>
        <select className="input w-48 text-xs" value={oltId}
          onChange={(e) => { const n = new URLSearchParams(params); e.target.value ? n.set('olt_id', e.target.value) : n.delete('olt_id'); n.delete('page'); setParams(n); }}>
          <option value="">Tous OLTs</option>
          {oltList?.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <label className="text-xs text-text-muted flex items-center gap-1.5">
          <input type="checkbox" checked={linkPending}
            onChange={(e) => { const n = new URLSearchParams(params); e.target.checked ? n.set('link_pending', 'true') : n.delete('link_pending'); n.delete('page'); setParams(n); }} />
          Link pending only
        </label>
        {activeFilterCount > 0 && (
          <button className="btn btn-sm text-warning" onClick={resetFilters} title="Réinitialiser tous les filtres">
            <X size={11} />Réinitialiser ({activeFilterCount})
          </button>
        )}
        <div className="ml-auto text-[11px] text-text-faint flex items-center gap-2">
          {isFetching && <RefreshCw size={10} className="animate-spin" />}
          page {page} / {data?.meta.last_page ?? '…'}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="luc-table">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.key} className={c.sortable ? 'cursor-pointer select-none hover:text-text' : ''} onClick={() => toggleSort(c)}>
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && (
                      sort === c.sortField
                        ? (dir === 'asc' ? <ArrowUp size={9} className="text-brand" /> : <ArrowDown size={9} className="text-brand" />)
                        : <ArrowUpDown size={9} className="opacity-30" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
            {filterRowOpen && (
              <tr className="bg-surface">
                {COLUMNS.map((c) => (
                  <th key={c.key} className="!py-1.5 !px-2">
                    {c.filterParam && c.filterKind === 'text' && (
                      <input
                        type="text"
                        className={`input h-6 text-[11px] ${c.filterWidth ?? 'w-full'}`}
                        placeholder={c.placeholder}
                        value={colFilters[c.filterParam] ?? ''}
                        onChange={(e) => setColFilters({ ...colFilters, [c.filterParam!]: e.target.value })}
                      />
                    )}
                    {c.filterParam && c.filterKind === 'select' && (
                      <select
                        className="input h-6 text-[11px] w-full"
                        value={colFilters[c.filterParam] ?? ''}
                        onChange={(e) => setColFilters({ ...colFilters, [c.filterParam!]: e.target.value })}
                      >
                        <option value="">tous</option>
                        <option value="up">up</option>
                        <option value="down">down</option>
                        <option value="discovering">discovering</option>
                      </select>
                    )}
                    {c.filterParam && c.filterKind === 'olt' && (
                      <input
                        type="text"
                        className="input h-6 text-[11px] w-full"
                        placeholder={c.placeholder}
                        value={colFilters[c.filterParam] ?? ''}
                        onChange={(e) => setColFilters({ ...colFilters, [c.filterParam!]: e.target.value })}
                      />
                    )}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {data?.data.map((o) => (
              <tr
                key={o.id}
                className="cursor-pointer hover:bg-bg-2"
                onClick={(e) => {
                  // Ne pas trigger si on a cliqué un lien interne (customer link) ou un bouton
                  const t = e.target as HTMLElement;
                  if (t.closest('a, button')) return;
                  navigate(`/app/onts/${o.id}`);
                }}
              >
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className={`dot ${o.oper_status === 'up' ? 'dot-up' : o.oper_status === 'down' ? 'dot-down' : 'dot-warn'}`} />
                    <span className="text-xs">{o.oper_status}</span>
                    {o.link_pending && <AlertTriangle size={10} className="text-warning" />}
                  </div>
                </td>
                <td>
                  <ServiceBadges data={o.data_service} voice={o.voice_service} />
                </td>
                <td>
                  {o.customer ? (
                    <Link to={`/app/customers/${o.customer.id}`} className="hover:text-brand">
                      {o.customer.name}
                    </Link>
                  ) : (
                    <span className="italic text-text-faint">non linké</span>
                  )}
                  {o.external_id && <span className="ml-1.5 font-mono text-[11px] text-text-faint">[{o.external_id}]</span>}
                </td>
                <td className="font-mono text-[11.5px]">{o.vendor_serial ?? '—'}</td>
                <td className="text-text-muted text-[11.5px] truncate max-w-xs">{o.model ?? '—'}</td>
                <td className="font-mono text-[11.5px] text-text-muted">
                  {o.olt?.name} <span className="text-text-faint">·</span> {o.parent?.vendor_local_id}<span className="text-text-faint">/</span>{o.vendor_local_id}
                </td>
                <td className="font-mono text-[11px] text-text-faint">{o.vendor_local_id}</td>
                <td className="font-mono text-[11px] text-text-faint">{new Date(o.last_seen_at).toLocaleString('fr')}</td>
              </tr>
            ))}
            {!data?.data.length && (
              <tr><td colSpan={8} className="text-center py-12 text-text-faint italic">Aucun ONT trouvé.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.meta.last_page > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-border bg-bg-2 shrink-0">
          <div className="text-xs text-text-faint">
            {((page - 1) * data.meta.per_page) + 1}-{Math.min(page * data.meta.per_page, data.meta.total)} / {data.meta.total}
          </div>
          <div className="flex gap-1.5">
            <button className="btn btn-sm" disabled={page <= 1}
              onClick={() => { const p = new URLSearchParams(params); p.set('page', String(page - 1)); setParams(p); }}>← Prev</button>
            <button className="btn btn-sm" disabled={page >= data.meta.last_page}
              onClick={() => { const p = new URLSearchParams(params); p.set('page', String(page + 1)); setParams(p); }}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Vert = service actif. Rouge = service suspendu. Gris = pas de service.
function ServiceBadges({ data, voice }: { data: 'active' | 'suspended' | null; voice: string | null }) {
  const dataColor = data === 'active' ? 'text-success' : data === 'suspended' ? 'text-danger' : 'text-text-faint';
  const voiceActive = voice && /^(registered|up|active)$/i.test(voice);
  const voiceSuspended = voice && /^(suspended|disabled|blocked)$/i.test(voice);
  const voiceColor = voiceActive ? 'text-success' : voiceSuspended ? 'text-danger' : voice ? 'text-warning' : 'text-text-faint';
  return (
    <div className="flex items-center gap-2 text-[11px]">
      <span className={`flex items-center gap-0.5 ${dataColor}`} title={data ? `Data: ${data}` : 'Pas de subscription data'}>
        <Cable size={10} />{data ? <Check size={10} /> : <X size={10} />}
      </span>
      <span className={`flex items-center gap-0.5 ${voiceColor}`} title={voice ? `Voice: ${voice}` : 'Pas de subscription voix'}>
        <Phone size={10} />{voice ? <Check size={10} /> : <X size={10} />}
      </span>
    </div>
  );
}
